import { Alert, Input, Layouts, Loader, TextArea, UploadImages } from 'components'
import s from './styles.module.scss'
import DashBoardNav from '../../dashboardNav'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import * as api from 'api'
import { head, imageCompressor } from 'helpers'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST

export default function Main() {
  const [loadingStatus, setLoadingStatus] = useState('Loading...')
  const [loading, setLoading] = useState(false)
  const [oldNews, setOldNews] = useState([])
  const [news, setNews] = useState([])
  const [oldNewsImages, setOldNewsImages] = useState([])
  const [oldNewsImagesUrls, setOldNewsImagesUrls] = useState([])
  const [oldImagesRemoved, setOldImagesRemoved] = useState([])
  const [newNewsImages, setNewNewsImages] = useState([])
  const [newNewsImagesUrls, setNewNewsImagesUrls] = useState([])

  const navigate = useNavigate()
  const processing = useRef(false)

  const updateOldNews = (field, value, index) => {
    const updated = [...oldNews]
    updated[index][field] = value
    setOldNews(updated)
  }

  const updateNewNews = (field, value, index) => {
    const updated = [...news]
    updated[index][field] = value
    setNews(updated)
  }

  const handleOldNewsImage = async (e, index) => {
    const file = Array.from(e.target.files)[0]
    if (!file) return
    const [compressed] = await imageCompressor([file], 'logo')
    if (!compressed) return

    const imageUrl = URL.createObjectURL(compressed)
    const updatedImages = [...oldNewsImages]
    updatedImages[index] = compressed
    setOldNewsImages(updatedImages)

    const updatedUrls = [...oldNewsImagesUrls]
    updatedUrls[index] = imageUrl
    setOldNewsImagesUrls(updatedUrls)
  }

  const handleNewNewsImage = async (e, index) => {
    const file = Array.from(e.target.files)[0]
    if (!file) return
    const [compressed] = await imageCompressor([file], 'logo')
    if (!compressed) return

    const imageUrl = URL.createObjectURL(compressed)
    const updatedImages = [...newNewsImages]
    updatedImages[index] = compressed
    setNewNewsImages(updatedImages)

    const updatedUrls = [...newNewsImagesUrls]
    updatedUrls[index] = imageUrl
    setNewNewsImagesUrls(updatedUrls)
  }

  const removeOldNews = index => {
    const removedImage = oldNews[index].image
    if (removedImage) setOldImagesRemoved(prev => [...prev, removedImage])
    setOldNews(prev => prev.filter((_, i) => i !== index))
    setOldNewsImages(prev => prev.filter((_, i) => i !== index))
    setOldNewsImagesUrls(prev => prev.filter((_, i) => i !== index))
  }

  const removeNewNews = index => {
    setNews(prev => prev.filter((_, i) => i !== index))
    setNewNewsImages(prev => prev.filter((_, i) => i !== index))
    setNewNewsImagesUrls(prev => prev.filter((_, i) => i !== index))
  }

  const fetchNews = useCallback(async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    const res = await api.admin.News.fetch({})
    if (res.code === 200) {
      setOldNews(res.payload.news)
      setOldNewsImages(Array(res.payload.news.length).fill(null))
      setOldNewsImagesUrls(res.payload.news.map(item => (item.image ? IMAGE_HOST + item.image : null)))
    } else {
      Alert.error(res.message)
    }

    setLoading(false)
    processing.current = false
  }, [])

  useEffect(() => {
    fetchNews()
  }, [fetchNews])

  const saveHandler = async () => {
    if (processing.current) return

    // Validation
    const allNews = [...oldNews, ...news]
    const isEmpty = allNews.some(
      x => !x.title?.trim() || !x.description?.trim() || !x.authorName?.trim()
    )
    if (isEmpty) return Alert.warn('All fields are required in each news item.')

    processing.current = true
    Alert.success('Saving News...')
    setLoading(true)

    const updatedOld = [...oldNews]
    const updatedNew = [...news]

    oldNewsImages.forEach((img, i) => {
      if (img) updatedOld[i].image = img.name
    })
    newNewsImages.forEach((img, i) => {
      if (img) updatedNew[i].image = img.name
    })

    const data = new FormData()
    data.append('oldNews', JSON.stringify(updatedOld))
    data.append('news', JSON.stringify(updatedNew))
    data.append('oldImagesRemoved', JSON.stringify(oldImagesRemoved))

    ;[...oldNewsImages, ...newNewsImages].forEach(img => {
      if (img) data.append('IMAGES', img)
    })

    const res = await api.admin.News.update(data)

    if (res.code === 201) {
      Alert.success(res.message)
      navigate(-1)
    } else {
      Alert.error(res.message)
    }

    setLoading(false)
    processing.current = false
  }

  useEffect(() => {
    head({ title: 'News | Admin' })
  }, [])

  return (
    <div className={s.main}>
      <div className={s.testimoniesOuter}>
        <Layouts.Classic title='News'>
          <div className={s.headerBottom}>
            <div className={s.addTestimony} onClick={saveHandler}>
              <span className='material-icons-outlined'>save</span> Save
            </div>
          </div>
        </Layouts.Classic>

        {!loading && (
          <div className={s.content + ' innerScrollX'}>
            <DashBoardNav />
            <div className={s.testimonies}>
              <div className={s.label}>News</div>

              {oldNews.map((item, i) => (
                <NewsItem
                  key={i}
                  index={i}
                  item={item}
                  imageUrl={oldNewsImagesUrls[i]}
                  onChange={updateOldNews}
                  onRemove={removeOldNews}
                  onImageChange={handleOldNewsImage}
                />
              ))}

              {news.map((item, i) => (
                <NewsItem
                  key={i}
                  index={i + oldNews.length}
                  item={item}
                  imageUrl={newNewsImagesUrls[i]}
                  onChange={(field, value) => updateNewNews(field, value, i)}
                  onRemove={() => removeNewNews(i)}
                  onImageChange={e => handleNewNewsImage(e, i)}
                />
              ))}
            </div>

            <div className={s.addMore}>
              <div
                onClick={() => {
                  setNews(prev => [...prev, { title: '', description: '', authorName: '', authorTitle: '', image: '' }])
                  setNewNewsImages(prev => [...prev, null])
                  setNewNewsImagesUrls(prev => [...prev, null])
                }}
              >
                Add News <span className='material-icons-outlined'>add_circle_outline</span>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className={s.loader}>
            <Loader message={loadingStatus} color='var(--c-primary)' colorText='var(--c-primary)' />
          </div>
        )}
      </div>
    </div>
  )
}

const NewsItem = ({ index, item, imageUrl, onChange, onRemove, onImageChange }) => (
  <div className={s.testimony}>
    <div className={s.title}>
      <span>News {index + 1}</span>
      <span onClick={() => onRemove(index)}>
        Remove<span className='material-icons-outlined'>cancel</span>
      </span>
    </div>

    <div className={s.imageUpload}>
      <UploadImages.Single
        userLogoUrl={imageUrl || ''}
        handleUserLogoChange={e => onImageChange(e, index)}
        id={`imageUpload${index}`}
      />
    </div>

    <TextArea.Classic
      label='Description'
      iconLeft='description'
      placeholder='Enter news description...'
      value={item.description}
      onChange={e => onChange('description', e.target.value, index)}
    />

    <Input.Classic
      type='text'
      label='Name'
      iconLeft='person'
      placeholder='Enter name'
      value={item.authorName}
      onChange={e => onChange('authorName', e.target.value, index)}
    />

    <Input.Classic
      type='text'
      label='Designation'
      iconLeft='work'
      placeholder='Enter designation'
      value={item.authorTitle}
      onChange={e => onChange('authorTitle', e.target.value, index)}
    />

    <Input.Classic
      type='text'
      label='Title'
      iconLeft='title'
      placeholder='Enter title'
      value={item.title}
      onChange={e => onChange('title', e.target.value, index)}
    />
  </div>
)
